import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  redirectionService,
  RedirectMatchType,
  RedirectType,
} from "@/services/redirectionService";
import { showToast } from "@/lib/toast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

export default function ModalCreateRedirection({
  isOpen,
  onClose,
  onCompleted,
}: Props) {
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [matchType, setMatchType] = useState<RedirectMatchType>(
    RedirectMatchType.EXACT
  );
  const [type, setType] = useState<RedirectType>(RedirectType.M301);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!source.trim() || !destination.trim()) {
      showToast.error("Vui lòng nhập đầy đủ Source và Destination");
      return;
    }
    setIsSubmitting(true);
    try {
      const res: any = await redirectionService.create({
        source,
        destination,
        matchType,
        type,
      });
      if (res?.ok || res?.status === 200 || res?.data?.status === "SUCCESS") {
        showToast.success("Tạo chuyển hướng thành công");
        onCompleted?.();
        onClose();
      } else {
        const msg =
          res?.message || res?.data?.message || "Tạo chuyển hướng thất bại";
        showToast.error(msg);
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || e?.message || "Tạo chuyển hướng thất bại";
      showToast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Thêm chuyển hướng</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Source</label>
            <Input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="/old-path"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Match type</label>
            <Select
              value={matchType}
              onValueChange={(v) => setMatchType(v as RedirectMatchType)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Chọn kiểu khớp" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={RedirectMatchType.EXACT}>
                  Khớp chính xác
                </SelectItem>
                <SelectItem value={RedirectMatchType.CONTAINS}>Chứa</SelectItem>
                <SelectItem value={RedirectMatchType.STARTS_WITH}>
                  Bắt đầu với
                </SelectItem>
                <SelectItem value={RedirectMatchType.ENDS_WITH}>
                  Kết thúc với
                </SelectItem>
                <SelectItem value={RedirectMatchType.REGEX}>Regex</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Destination
            </label>
            <Input
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="/new-path"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <Select
              value={String(type)}
              onValueChange={(v) => setType(Number(v) as RedirectType)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Chọn mã" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="301">301 Permanent Move</SelectItem>
                <SelectItem value="302">302 Temporary Move</SelectItem>
                <SelectItem value="307">307 Temporary Redirect</SelectItem>
                <SelectItem value="410">410 Content Deleted</SelectItem>
                <SelectItem value="451">
                  451 Content Unavailable for Legal Reasons
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            Tạo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
